import { Button, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import PolicyService from '../../../services/Policy.service';
import { ROUTER } from '../../../constants/router';
import ListPrivacy from '../Shared/ListPrivacy';
import useNotify from '../../../context/Notification/useNotify';
import { useForm } from 'antd/lib/form/Form';
import DocTabs from './components/DocTabs';
import { usePrivacytoString } from './components/PrivacytoString';
import { usePrivacytoStringEn } from './components/PrivacytoStringEn';
import { PlusOutlined } from '@ant-design/icons';
import GlobalService from '../../../services/Global.service';
import { calculateImageWidth } from '../../../util/util';
import usePermission from '../../../hooks/usePermission';

const PrivacyNotices = ({ selectType }) => {
    const { t, i18n } = useTranslation(['translation', 'hr-policy']);
    let lang = i18n.language;
    const [data, setData] = useState([]);
    const [currentSelectList, setCurrentSelectList] = useState();
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState();
    const [detailPublished, setDetailPublished] = useState();
    // Logo
    const [logo, setLogo] = useState('');
    const [logoPublish, setLogoPublish] = useState('');
    const getImage = async (companyLogoId) => {
        try {
            const { data } = await GlobalService.getImage(companyLogoId);
            if (data.resultData?.imageId) {
                return `data:image/png;base64,${data.resultData?.imageId}`;
            }
        } catch (error) {
            setLogo('');
            setLogoPublish('');
            console.log(error);
        }
    };

    useEffect(() => {
        const getPolicyNotice = async () => {
            setLoading(true);
            try {
                const { data } = await PolicyService.getPolicyNotice(
                    currentSelectList?.privacyPolicyNoticeId
                );
                if (data?.resultData) {
                    setDetail(data?.resultData);
                    setLoading(false);
                    // Logo
                    setLogo('');
                    if (
                        data?.resultData?.personalDataCollection?.companyLogoId
                    ) {
                        getImage(
                            data?.resultData?.personalDataCollection
                                ?.companyLogoId
                        ).then((logo) => setLogo(logo));
                    }
                } else setLoading(false);
            } catch (error) {
                console.log(error);
                setLoading(false);
            }
        };
        const getPublishedVersion = async () => {
            try {
                const { data } = await PolicyService.getPublishedVersion(
                    currentSelectList?.privacyPolicyNoticeId
                );
                if (data?.resultData) {
                    const parse = JSON.parse(data?.resultData?.data);
                    setDetailPublished(parse);
                    // Logo
                    setLogoPublish('');
                    if (parse?.personalDataCollection?.companyLogoId) {
                        getImage(
                            parse?.personalDataCollection?.companyLogoId
                        ).then((logo) => setLogoPublish(logo));
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (currentSelectList?.privacyPolicyNoticeId) getPolicyNotice();
        if (
            currentSelectList?.hasChanged &&
            currentSelectList?.status === 'Published'
        )
            getPublishedVersion();
        else setDetailPublished();
    }, [currentSelectList]);

    let navigate = useNavigate();

    const getPolicyList = async () => {
        const query = {
            dataSubjectTypeIds: selectType ? selectType : '',
        };
        const { data } = await PolicyService.getPolicyList(query);
        if (data.resultData) {
            setData(data.resultData);
            setCurrentSelectList(data.resultData[0]);
        }
    };

    useEffect(() => {
        getPolicyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectType]);

    const notify = useNotify();
    const [form] = useForm();

    const handelRename = (values) => {
        setLoading(true);
        if (currentSelectList?.privacyPolicyNoticeId) {
            const payload = {
                privacyNoticeId: currentSelectList?.privacyPolicyNoticeId,
                privacyNoticeNameEn: values.nameEn,
                privacyNoticeNameTh: values.nameTh,
            };
            setTimeout(async () => {
                try {
                    const { data } = await PolicyService.rename(payload);
                    if (data.resultCode === 1) {
                        form.resetFields();
                        notify({
                            title: t('common.Success'),
                            description: t('Successfully saved data'),
                            timeOut: 1800,
                        });
                        setTimeout(() => {
                            setLoading(false);
                            getPolicyList();
                        }, 1300);
                    } else {
                        setLoading(false);
                        notify({
                            type: 'error',
                            title: t('common.Fail'),
                            description: t('Failed to rename'),
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    notify({
                        type: 'error',
                        title: t('common.Fail'),
                        description: t('Failed to rename'),
                    });
                }
            }, 1000);
        }
    };

    const width1 = calculateImageWidth(logo);
    const width2 = calculateImageWidth(logoPublish);

    const { html } = usePrivacytoString(detail, logo, width1);
    const { html: htmlEn } = usePrivacytoStringEn(detail, logo, width1);

    const { html: htmlPublish } = usePrivacytoString(
        detailPublished,
        logoPublish,
        width2
    );
    const { html: htmlPublishEn } = usePrivacytoStringEn(
        detailPublished,
        logoPublish,
        width2
    );

    const { isAuditor } = usePermission();

    return (
        <Layout>
            <div className="flex items-center my-4">
                <div className="flex-1 font-bold text-xl">
                    {t('policy.Document')}
                </div>
                <Button
                    disabled={isAuditor}
                    type="primary"
                    size="large"
                    onClick={() => {
                        navigate(`${ROUTER.POLICY.PRIVACY_NOTICES}/new#step1`);
                    }}
                    icon={<PlusOutlined />}
                >
                    {t('Add Policy')}
                </Button>
            </div>
            <div className="grid grid-cols-7 gap-4">
                <ListPrivacy
                    data={data}
                    pathEdit={ROUTER.POLICY.PRIVACY_NOTICES}
                    stepEdit={'#step8'} // step preview
                    currentSelectName={{
                        nameTh: detail?.personalDataCollection
                            ?.privacyPolicyNoticeNameTh,
                        nameEn: detail?.personalDataCollection
                            ?.privacyPolicyNoticeNameEn,
                        namePublishTh:
                            detailPublished?.personalDataCollection
                                ?.privacyPolicyNoticeNameTh,
                        namePublishEn:
                            detailPublished?.personalDataCollection
                                ?.privacyPolicyNoticeNameEn,
                        placeholderNameEN: t('PlaceholderNameEN', {
                            ns: 'hr-policy',
                        }),
                        placeholderNameTH: t('PlaceholderNameTH', {
                            ns: 'hr-policy',
                        }),
                    }}
                    linkName="external-privacy"
                    getPolicyList={getPolicyList}
                    setCurrentSelectList={setCurrentSelectList}
                    currentSelectList={currentSelectList}
                    form={form}
                    // Parameters Name
                    policyObject={{
                        privacyName:
                            lang === 'en'
                                ? 'privacyPolicyNoticeNameEn'
                                : 'privacyPolicyNoticeNameTh',
                        nameTh: 'privacyPolicyNoticeNameTh',
                        nameEn: 'privacyPolicyNoticeNameEn',
                        privacyType:
                            lang === 'en'
                                ? 'privacyPolicyTypeEn'
                                : 'privacyPolicyTypeTh',
                        privacyId: 'privacyPolicyNoticeId',
                        privacyCompany:
                            lang === 'en' ? 'companyNameEn' : 'companyNameTh',
                    }}
                    handelRename={(value) => handelRename(value)}
                    historyObject={{
                        serviceHistory: PolicyService.getHistories,
                        type: 'privacy',
                        historyParamName: 'privacyNoticeId', // ชื่อ payload ของ id ที่ service get history เช่น hrPrivacyPolicyId: "1234" ที่ต้องใส่คือ: hrPrivacyPolicyId
                        historyResultIdName: 'privacyNoticeHistoryId', // ได้หลังจาก service get history ใน result ชื่อ parameter ของ id เช่น { "hrPrivacyPolicyHistoryId" : 1234 } ที่ต้องใส่คือ: hrPrivacyPolicyHistoryId
                    }}
                    deleteObject={{
                        serviceDelete: PolicyService.delete,
                        title: lang === 'en' ? 'Confirm Delete' : 'ยืนยันการลบ',
                        text:
                            currentSelectList?.status === 'Draft'
                                ? lang === 'en'
                                    ? `Do you want to delete draft version ${currentSelectList?.version} ?`
                                    : `คุณต้องการลบรายการแบบร่างเวอร์ชัน ${currentSelectList?.version} ?`
                                : lang === 'en'
                                ? `<span style='color:#F0536F'>The use of the URL link for this Privacy Notice will still be available, but cannot be edited anymore.</span>\nDo you want to delete publish version ${currentSelectList?.latestPublishVersion} ?`
                                : `<span style='color:#F0536F'>การใช้งาน URL link นโยบายความเป็นส่วนตัวนี้จะยังคงใช้งานได้ แต่จะไม่สามารถแก้ไขนโยบายความเป็นส่วนตัวได้อีก</span>\nคุณต้องการลบรายการเผยแพร่เวอร์ชัน ${currentSelectList?.latestPublishVersion} ?`,
                    }}
                    // pop up ของ confirm ก่อน delete
                    confirmObject={{
                        title:
                            lang === 'en'
                                ? 'There may be websites that use this Privacy Notice.'
                                : 'อาจมีเว็บไซต์ใช้นโยบายความเป็นส่วนตัวนี้อยู่',
                        text:
                            lang === 'en'
                                ? `Before deleting the data, please check that no website is using this Privacy Notice.`
                                : `ก่อนลบข้อมูล โปรดตรวจสอบว่าไม่มีเว็บไซต์ใดใช้\nนโยบายความเป็นส่วนตัวนี้`,
                    }}
                    loading={loading}
                    templateBody={{ html, htmlEn, htmlPublish, htmlPublishEn }}
                >
                    {(previewLang) => (
                        <DocTabs
                            logo={logo}
                            logoPublish={logoPublish}
                            detailPublished={detailPublished}
                            detail={detail}
                            loading={loading}
                            currentSelectList={currentSelectList}
                            previewLang={previewLang}
                            privacyPolicyNoticeId={
                                currentSelectList?.privacyPolicyNoticeId
                            }
                        />
                    )}
                </ListPrivacy>
            </div>
        </Layout>
    );
};

export default PrivacyNotices;
